import type { Lead } from '@/types/crm'
import { companyDomain, leadSlug } from '@/features/leads/normalise'

/**
 * Which CRM lead a Skida PRO company is — 21 Oct 2026.
 *
 * The bridge is the company, never a guide: guides move between companies and
 * between their own addresses, while a company's billing profile carries the
 * one identifier nobody types twice differently — its VAT number. So the
 * evidence is tried strongest first:
 *
 *   vat     the billing profile's VAT number is the lead's org number
 *   domain  the company's own email domain is one of the lead's domains
 *   name    the company's name, or its legal name, is the lead's name
 *
 * Nothing here links anything. It suggests, and a rep presses Link. A lead
 * that is already linked to another company is never suggested.
 */

/**
 * What matching reads of a company. `ProCompany` fits it; kept structural so
 * this file pulls in no Firebase and `scripts/checkProLeadMatch.ts` can run it.
 */
export interface MatchCompany {
  id: string
  name: string
  email: string
  billing: { vatNumber: string; companyName: string } | null
}

export type MatchMethod = 'vat' | 'domain' | 'name'

export interface LeadSuggestion {
  lead: Lead
  method: MatchMethod
  why: string
}

/** The lead's own link: the functions' mirror first, then the rep's. */
export function linkedCompanyId(l: Lead): string | null {
  return l.skidaProCompanyId || l.proLink?.companyId || null
}

/**
 * The digits of a VAT or org number, without country prefix or suffix.
 *
 * `NO 123 456 789 MVA` → `123456789`, `SE556677889901` → `556677889901`,
 * `556677-8899` → `5566778899`. Short runs are not identifiers and return ''.
 */
export function idDigits(v: string): string {
  const d = v.replace(/\D/g, '')
  return d.length >= 6 ? d : ''
}

/**
 * Does a VAT number name this org number?
 *
 * Equal digits, or the Swedish form — the VAT number is the org number with
 * `01` after it.
 */
export function vatMatchesOrg(vat: string, org: string): boolean {
  const v = idDigits(vat)
  const o = idDigits(org)
  if (!v || !o) return false
  return v === o || v === `${o}01`
}

/** The org number a VAT number carries — its digits, less Sweden's `01`. */
export function orgFromVat(vat: string): string {
  const d = idDigits(vat)
  return /^SE/i.test(vat.trim()) && d.length === 12 && d.endsWith('01') ? d.slice(0, 10) : d
}

function leadDomains(l: Lead): Set<string> {
  const out = new Set(l.contactDomains.map((d) => d.toLowerCase()))
  const site = l.website ? companyDomain(l.website) : null
  if (site) out.add(site)
  return out
}

export function suggestLeads(c: MatchCompany, leads: Lead[]): LeadSuggestion[] {
  const free = leads.filter((l) => !linkedCompanyId(l))
  const out: LeadSuggestion[] = []
  const seen = new Set<string>()
  const add = (lead: Lead, method: MatchMethod, why: string) => {
    if (seen.has(lead.id)) return
    seen.add(lead.id)
    out.push({ lead, method, why })
  }

  const vat = c.billing?.vatNumber ?? ''
  if (idDigits(vat)) {
    for (const l of free) {
      if (vatMatchesOrg(vat, l.enrichment.orgNumber)) add(l, 'vat', `VAT ${vat} = org. no. ${l.enrichment.orgNumber}`)
    }
  }

  const domain = c.email ? companyDomain(c.email) : null
  if (domain) {
    for (const l of free) if (leadDomains(l).has(domain)) add(l, 'domain', `Company email domain ${domain}`)
  }

  const names = new Set([c.name, c.billing?.companyName ?? ''].filter((n) => n.trim()).map(leadSlug))
  for (const l of free) {
    if (names.has(l.id) || names.has(leadSlug(l.name))) add(l, 'name', 'Same company name')
  }
  return out
}
